import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { PackageCheck, Loader2, Plus, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inventoryApi } from "./inventoryApi";
import { formatCurrency, formatDateTime } from "./inventoryConstants";
import { ReceiveDialog } from "./ReceiveDialog";

export const ReceivingSection = ({ currentUser, canManage, vehicles }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [showReceive, setShowReceive] = useState(false);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try { setReceipts(await inventoryApi.getReceipts()); } catch { toast.error("Failed to load receipts"); }
    setLoading(false);
  }, []);

  useEffect(() => { inventoryApi.getVendors().then(setVendors).catch(() => {}); }, []);
  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  return (
    <div className="space-y-4" data-testid="receiving-section">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{receipts.length} receipts</span>
        {canManage && <Button onClick={() => setShowReceive(true)} data-testid="standalone-receive-btn"><Plus className="w-4 h-4 mr-1" /> Receive (no PO)</Button>}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : receipts.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><PackageCheck className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No receipts yet</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {receipts.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`receipt-${r.id}`}>
              <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center shrink-0"><PackageCheck className="w-4 h-4 text-green-600" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-slate-900 truncate">{r.vendor_name}</span>
                  {r.invoice_number && <span className="text-[10px] text-slate-400 shrink-0">Inv {r.invoice_number}</span>}
                  {r.invoice_upload && <FileText className="w-3.5 h-3.5 text-blue-500 shrink-0" />}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">{formatDateTime(r.received_date)} · {r.lines?.length || 0} line(s){r.core_total > 0 ? ` · Core ${formatCurrency(r.core_total)}` : ""}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-slate-900 tabular-nums">{formatCurrency(r.grand_total)}</div>
                <div className="text-[10px] text-slate-400">Tax {formatCurrency(r.tax)} · Frt {formatCurrency(r.freight)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ReceiveDialog open={showReceive} onClose={() => setShowReceive(false)} po={null} vendors={vendors} vehicles={vehicles} currentUser={currentUser} onReceived={fetchReceipts} />
    </div>
  );
};
